import { Button, Textarea } from '@patchbay/ui'
import * as React from 'react'

type Rating = 'good' | 'ok' | 'bad'
type State = { kind: 'loading' } | { kind: 'ready'; rating: Rating } | { kind: 'done'; rating: Rating } | { kind: 'error'; message: string }

const LABEL: Record<Rating, string> = { good: 'Good', ok: 'OK', bad: 'Bad' }

async function message(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: string; code?: string }
    if (body.code === 'survey_already_answered') return 'This rating was already submitted.'
    if (body.code === 'survey_link_expired') return 'This rating link has expired.'
  } catch {
    // The same generic message is safe for malformed and unknown links.
  }
  return 'This rating link is not available.'
}

export function PublicSurvey({ initialToken }: { initialToken: string }) {
  const [token] = React.useState(initialToken)
  const [state, setState] = React.useState<State>({ kind: 'loading' })
  const [comment, setComment] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    window.history.replaceState(window.history.state, '', '/survey')
    const controller = new AbortController()
    void fetch(`/api/v1/satisfaction/public?t=${encodeURIComponent(token)}`, {
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) return setState({ kind: 'error', message: await message(response) })
        const body = (await response.json()) as { rating: Rating }
        setState({ kind: 'ready', rating: body.rating })
      })
      .catch(() => setState({ kind: 'error', message: 'The rating page could not be reached. Please try again.' }))
    return () => controller.abort()
  }, [token])

  const submit = async (rating: Rating) => {
    if (submitting) return
    setSubmitting(true)
    try {
      const response = await fetch('/api/v1/satisfaction/public', {
        method: 'POST',
        credentials: 'omit',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, comment: comment.trim() || null }),
      })
      if (!response.ok) return setState({ kind: 'error', message: await message(response) })
      setState({ kind: 'done', rating })
    } catch {
      setState({ kind: 'error', message: 'The rating could not be submitted. Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-muted/30 p-4">
      <div className="w-full max-w-lg space-y-5 rounded-xl border border-border bg-card p-6 shadow-sm">
        <div>
          <p className="text-sm font-semibold tracking-wide text-primary uppercase">Ewee</p>
          <h1 className="mt-1 text-2xl font-semibold">How was your experience?</h1>
        </div>
        {state.kind === 'loading' ? <p className="text-sm text-muted-foreground">Checking your rating link…</p> : null}
        {state.kind === 'error' ? (
          <p role="alert" className="text-sm text-warning">
            {state.message}
          </p>
        ) : null}
        {state.kind === 'done' ? (
          <div role="status" className="space-y-2">
            <p className="text-lg font-medium">Thank you.</p>
            <p className="text-sm text-muted-foreground">Your {LABEL[state.rating]} rating was recorded.</p>
          </div>
        ) : null}
        {state.kind === 'ready' ? (
          <div className="space-y-4">
            <p className="text-sm">
              You chose <strong>{LABEL[state.rating]}</strong>. Submit to record it.
            </p>
            <label className="grid gap-1.5 text-sm">
              Comment (optional)
              <Textarea
                rows={4}
                value={comment}
                maxLength={2000}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Anything you would like us to know?"
              />
            </label>
            <Button variant="primary" disabled={submitting} onClick={() => void submit(state.rating)}>
              {submitting ? 'Submitting…' : `Submit ${LABEL[state.rating]}`}
            </Button>
          </div>
        ) : null}
      </div>
    </main>
  )
}
